// Company statuses shown in the Status column badge and the Status filter.
// Codes are snake_case to match the CSS badge modifier classes (.fh-status--<code>).
// The service normalizes the backend's UPPER_CASE values (e.g. "UNVERIFIED") to these codes.
export const COMPANY_STATUS = {
  UNVERIFIED:                            'unverified',
  VERIFIED:                              'verified',
  PARTIALLY_VERIFIED:                    'partially_verified',
  AWAITING_COMPLIANCE_VALIDATION:        'awaiting_compliance_validation',
  AWAITING_FINANCIAL_VALIDATION:         'awaiting_financial_validation',
  AWAITING_MANUAL_COMPLIANCE_VALIDATION: 'awaiting_manual_compliance_validation',
  WAITING_FOR_KAM:                       'waiting_for_kam',
  DRAFT:                                 'draft',
  BLOCKED:                               'blocked'
} as const;
export type CompanyStatus = typeof COMPANY_STATUS[keyof typeof COMPANY_STATUS];

// Display metadata for each status code — `label` is shown in the Status column badge and the
// Status filter; `cssClass` is the snake_case code used to build the badge CSS modifier class
// (e.g. `fh-status--<cssClass>` in the table, `cd-status--<cssClass>` in the detail view).
export interface CompanyStatusMeta {
  label: string;
  cssClass: string;
}

export const COMPANY_STATUS_LABELS: Record<CompanyStatus, CompanyStatusMeta> = {
  [COMPANY_STATUS.UNVERIFIED]: {
    label: 'Unverified',                             
    cssClass: 'unverified'
  },
  [COMPANY_STATUS.VERIFIED]: {
    label: 'Verified',                               
    cssClass: 'verified'
  },
  [COMPANY_STATUS.PARTIALLY_VERIFIED]: {
    label: 'Partially Verified',                     
    cssClass: 'partially_verified'
  },
  [COMPANY_STATUS.AWAITING_COMPLIANCE_VALIDATION]: {
    label: 'Awaiting compliance validation',         
    cssClass: 'awaiting_compliance_validation'
  },
  [COMPANY_STATUS.AWAITING_FINANCIAL_VALIDATION]: {
    label: 'Awaiting financial validation',          
    cssClass: 'awaiting_financial_validation'
  },
  [COMPANY_STATUS.AWAITING_MANUAL_COMPLIANCE_VALIDATION]: {
    label: 'Awaiting manual compliance validation',  
    cssClass: 'awaiting_manual_compliance_validation'
  },
  [COMPANY_STATUS.WAITING_FOR_KAM]: {
    label: 'Waiting for KAM',                        
    cssClass: 'waiting_for_kam'
  },
  [COMPANY_STATUS.DRAFT]: {
    label: 'Draft',                                  
    cssClass: 'draft'
  },
  [COMPANY_STATUS.BLOCKED]: {
    label: 'Blocked',                                
    cssClass: 'blocked'
  },
};

// Company types shown in the Company type column and the Company type filter.
export const COMPANY_TYPE = {
  MAKER:           'maker',
  RETAILER:        'retailer',
  MAKER_RETAILER:  'maker_retailer',
  AGENCY:          'agency',
  SERVICE_PROVIDER:'service_provider',
} as const;
export type CompanyType = typeof COMPANY_TYPE[keyof typeof COMPANY_TYPE];

// Display labels for each company type code.
export const COMPANY_TYPE_LABELS: Record<CompanyType, string> = {
  [COMPANY_TYPE.MAKER]:            'Maker',
  [COMPANY_TYPE.RETAILER]:         'Retailer',
  [COMPANY_TYPE.MAKER_RETAILER]:   'Maker & Retailer',
  [COMPANY_TYPE.AGENCY]:           'Agency',
  [COMPANY_TYPE.SERVICE_PROVIDER]: 'Service Provider/Partnerships',
};

// Max characters shown for text columns before truncating with an ellipsis.
// The full text is revealed via a tooltip on hover.
export const COMPANY_TEXT_MAX_LENGTH = {
  name:         30,
  companyId:    12,
  type:         28,
  taxId:        14,
  commercialOwner: 24,
} as const;

// Sentinel value shown in the Commercial owner column when a company has more than one owner.
// Rendered as a blue link (see .fh-owner-link) instead of a plain email.
export const COMPANY_MULTIPLE_OWNERS = 'Multiple';

// Placeholder shown in table cells when the backend value is empty (e.g. Tax id, Commercial owner).
export const COMPANY_EMPTY_VALUE = '-';

// Permission slugs that grant access to the Companies tab. The tab is visible when the user
// has ANY of these (or is an admin — handled by HasPermissionsService.checkPermission).
export const COMPANY_PERMISSIONS = {
  KAM_CREATOR:      'financial-hub/companies/kam-creator',
  FINANCE_APPROVER: 'financial-hub/companies/finance-approver',
} as const;

// Query param names used on the companies routes (list → detail navigation).
// `country` is read by CountryQueryParamGuard to switch the country context before the detail loads.
export const COMPANY_QUERY_PARAM = {
  COUNTRY: 'country',
  FROM: 'from',
} as const;

export const COMPANY_QUERY_FROM = {
  LIST: 'list',
} as const;

